import { styled, useTheme, StepContextType, useStepContext as useMuiStepContext } from '@mui/material'
import { StepIconProps } from '@mui/material/StepIcon'
import { Check } from 'react-feather'
import { BaseStepLabel, Typography } from 'ComponentLibrary/index'
import { type BaseStepLabelProps } from 'ComponentLibrary/src/atoms/StepLabel/BaseStepLabel'

/**
 * Expects a 0-based step number and a label string.
 */
export function NumberStepLabel({
    step,
    label,
    ...props
}: Omit<BaseStepLabelProps, 'StepIconComponent' | 'children'> & { step: number; label: string }) {
    const theme = useTheme()
    const stepContext = useStepContext()
    const stepIcon = (props: StepIconProps) => <NumberStepIcon step={step} {...props} />
    return (
        <BaseStepLabel {...props} StepIconComponent={stepIcon} style={{ marginLeft: step !== 0 ? 4 : undefined }}>
            <Typography
                style={{ marginLeft: '16px', marginRight: '20px' }}
                color={
                    stepContext?.active || stepContext?.completed ? theme.palette.customtext.darkblue100 : theme.palette.customtext.black38
                }
            >
                {label}
            </Typography>
        </BaseStepLabel>
    )
}

function NumberStepIcon(props: Omit<StepIconProps, 'icon'> & { step: number }) {
    const theme = useTheme()
    const { active, completed, className, step } = props

    return (
        <NumberStepIconRoot
            backgroundColor={active || completed ? theme.palette.primary2.green100 : theme.palette.customtext.black38}
            color={completed ? theme.palette.white : theme.palette.customtext.black38}
            className={className}
        >
            {completed ? (
                <Check data-testid="check-icon" />
            ) : (
                <TypographyWithDot color={theme.palette.white} variant={'bodyTextLarge'}>
                    {`${step + 1}`}
                </TypographyWithDot>
            )}
        </NumberStepIconRoot>
    )
}

const NumberStepIconRoot = styled(Typography)<{ color: string; backgroundColor: string }>(({ color, backgroundColor }) => ({
    color,
    display: 'flex',
    height: 32,
    width: 32,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor,
    borderRadius: '50%',
}))

const TypographyWithDot = styled(Typography)`
    &::after {
        content: '.';
        margin-right: -3px;
    }
`

/**
 * Wraps MUI's useStepContext hook to throw an error if used outside of a StepContextProvider.
 * Motivation, the type definition is {} | StepContextType, so typescript doesn't know if it's defined or not.
 */
export function useStepContext() {
    const context = useMuiStepContext()
    if (isStepContext(context)) {
        return context
    }
}

function isStepContext(context: NonNullable<unknown> | StepContextType): context is StepContextType {
    return 'completed' in context && 'active' in context
}
